/**
 * ReservationsResultCard — the /eat command's "Where you eat" card.
 *
 * Fetches the scan's reservations (calendar-derived + booking-email
 * confirmations) live on render, merges them (calendar wins on timing),
 * and shows the ReservationsReport.
 */
import { useEffect, useState } from 'react';
import { apiFetch } from '@/lib/apiClient';
import { useSession } from '@/features/auth/hooks/useSession';
import { getActiveWorkspaceId } from '@/lib/api/workspace-context';
import { ReservationsReport } from '../ReservationsReport';
import type { ReservationRow } from '../ReservationsReport';

interface DiscoveryStatusSlim {
  jobs?: {
    calendar?: { restaurantReservations?: ReservationRow[] };
    inbox?: { restaurantReservations?: ReservationRow[] };
  };
}

export function ReservationsResultCard() {
  const [rows, setRows] = useState<ReservationRow[] | null>(null);
  const [failed, setFailed] = useState(false);
  const { token } = useSession();

  useEffect(() => {
    if (!token) return;
    let alive = true;
    (async () => {
      try {
        const wsId = getActiveWorkspaceId();
        const data = await apiFetch<DiscoveryStatusSlim>(
          `/workspaces/${wsId}/onboarding/discovery-status`,
          { authToken: token },
        );
        const calRes = data?.jobs?.calendar?.restaurantReservations ?? [];
        const mailRes = data?.jobs?.inbox?.restaurantReservations ?? [];
        const calKeys = new Set(calRes.map((r) => `${r.name.toLowerCase()}|${new Date(r.whenISO).toDateString()}`));
        const merged = [
          ...calRes,
          ...mailRes.filter((r) => !calKeys.has(`${r.name.toLowerCase()}|${new Date(r.whenISO).toDateString()}`)),
        ];
        if (alive) setRows(merged);
      } catch {
        if (alive) setFailed(true);
      }
    })();
    return () => { alive = false; };
  }, [token]);

  if (failed) {
    return <p>Reservation data isn&apos;t available yet — connect your accounts and let a sync finish.</p>;
  }
  if (!rows) return null;
  if (rows.length === 0) return <p>No restaurant reservations found over the last year.</p>;
  return <ReservationsReport reservations={rows} />;
}
